<template>
  <VDialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >

    <template v-slot:activator="{ props }">
      <div v-bind="props" >
        <slot></slot>
      </div>
    </template>

    <VForm ref="form" v-on:submit.prevent="formSubmit()">
      <VCard v-cloak >
        <VCardTitle class="bg-primary d-flex justify-space-between">
          {{$t('account.changeOwnPassword')}}

          <VBtn icon @click="dialog = false">
            <VIcon>mdi-close</VIcon>
          </VBtn>
        </VCardTitle>

        <VCardText scrollable class="scrollable pt-4 pb-0">
          <VTextField
            v-model="oldPassword"
            v-bind:rules="oldPasswordRules"
            v-bind:label="$t('account.oldPassword')"
            v-bind:error-messages="oldPasswordError"
            type="password"
            required block variant="solo-filled" flat
            data-cy="dialog-changePassword-password"
          >
          </VTextField>

          <VTextField
            v-model="newPassword"
            type="password"
            v-bind:rules="newPasswordRules"
            v-bind:label="$t('account.newPassword')"
            v-bind:error-messages="newPasswordError"
            v-bind:required="newPassword || repeatedNewPassword"
            block variant="solo-filled" flat
            data-cy="dialog-changePassword-new-password"
          >
          </VTextField>

          <VTextField
            v-model="repeatedNewPassword"
            type="password"
            v-bind:rules="[passwordConfirmationRule]"
            v-bind:label="$t('account.repeatNewPassword')"
            v-bind:error-messages="repeatedNewPasswordError"
            v-bind:required="repeatedNewPassword || newPassword"
            block variant="solo-filled" flat
            data-cy="dialog-changePassword-repeated-password"
          >
          </VTextField>

          <div v-if="errorMessage || successMessage">
            <div v-show="successMessage" class="bg-success" data-cy="podMenu-generatedLabels-successMessage">
              {{successMessage}}
            </div>

            <div v-if="errorMessage" class="bg-error" data-cy="dialog-exportSubtitle-fileTypeError">
              {{errorMessage}}
            </div>
          </div>
        </VCardText>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn type="submit" class="bg-primary" elevation=0  data-cy="dialog-changePassword-submit">{{$t('account.changePassword')}}</v-btn>
          <v-btn variant="text" @click="dialog = false" data-cy="dialog-changePassword-false">{{$t('common.cancel')}}</v-btn>
        </v-card-actions>
      </VCard>
    </VForm>

  </VDialog>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore'
import Helper from '../../../../components/helper.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';

export default {
  mixins: [DialogActionKeys],
  name: "ChangePasswordDialog",
  data() {
    return {
      dialog: false,
      newPassword: '',
      oldPassword: '',
      repeatedNewPassword: '',
      oldPasswordRules: [
        (v) => {
          if (typeof v !== 'string' || v.trim() === '') {
            return this.$t('menus.createUser.passwordRequired');
          }

          return true;
        },
      ],
      newPasswordRules: Helper.getPasswordRules(this),

      newPasswordError: '',
      oldPasswordError: '',
      repeatedNewPasswordError: '',

      errorMessage: '',
      successMessage: '',
    };
  },
  watch: {
    dialog(value) {
      if (!value) {
        this.reset();
      }
    },
  },
  methods: {
    async formSubmit() {
      const { valid } = await this.$refs.form.validate(); // Await validation result
      if (!valid) {
        return;
      }

      this.newPasswordError = '';
      this.oldPasswordError = '';
      this.repeatedNewPasswordError = '';
      this.errorMessage = ''; // Clear previous messages
      this.successMessage = '';

      const oldPassword = this.oldPassword.trim();
      if (oldPassword.length === 0) {
        this.oldPasswordError = this.$t('account.requiredPassword');
        return;
      }

      const newPassword = this.newPassword.trim();
      if (newPassword.length === 0) {
        this.newPasswordError = this.$t('account.requiredPassword');
        return;
      }

      const repeatedNewPassword = this.repeatedNewPassword.trim();
      if (repeatedNewPassword.length === 0) {
        this.repeatedNewPasswordError = this.$t('account.requiredPassword');
        return;
      }

      if (newPassword !== repeatedNewPassword) {
        this.repeatedNewPasswordError = this.$t('account.repeatedPasswordNotMatch');
        return;
      }

      const payload = {
        oldPassword,
        newPassword,
        repeatedNewPassword
      }

      const authStore = useAuthStore();

      try { // Start try block
        await authStore.changePassword(payload); // Await the async operation

        this.reset(); // Use this directly
        this.successMessage = this.$t('account.passwordChanged'); // Use this directly
      } catch (error) { // Start catch block
        let localizedMessage;
        switch (error.code) {
          case 'NotAuthorizedException':
            localizedMessage = this.$t('account.wrongPassword'); // Use this directly
            break;

          case 'LimitExceededException':
            localizedMessage = this.$t('account.passwordChangeLimitExceeded'); // Use this directly
            break;

          default:
            localizedMessage = this.$t('backend.system-error'); // Use this directly
            break;
        }

        this.errorMessage = localizedMessage; // Use this directly
      }
    },
    reset() {
      this.resetPasswordInputFields();

      this.newPasswordError = '';
      this.oldPasswordError = '';
      this.repeatedNewPasswordError = '';
      this.errorMessage = '';
      this.successMessage = '';

      this.$refs.form.reset();
    },
    resetPasswordInputFields() {
      this.newPassword = '';
      this.oldPassword = '';
      this.repeatedNewPassword = '';
    }
  },
  computed: {
    passwordConfirmationRule() {
      return this.newPassword === this.repeatedNewPassword || this.$t('menus.createUser.passwordsNotMatch');
    },
  }
}
</script>
